import { zodResolver } from '@hookform/resolvers/zod'
import { Ellipsis } from 'lucide-react'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { DatePicker, fromIsoDate, toIsoDate } from '@/components/ui/date-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { DocumentData } from '@/interfaces/DocumentData'
import API from '@/lib/api'

const schema = z.object({
  paidAt: z.string(),
})

type MarkDocumentPaidFormData = z.infer<typeof schema>

export function MarkDocumentPaidForm({
  document,
  linkSegment,
}: {
  document: DocumentData
  linkSegment: 'invoice' | 'expense' | 'order'
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'documentDetail',
  })

  const form = useForm<MarkDocumentPaidFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      paidAt: document.paidAt
        ? moment(document.paidAt).toISOString()
        : new Date().toISOString(),
    },
  })

  const onSubmit = (data: MarkDocumentPaidFormData) => {
    API.post(
      `/api/v2/paid-${linkSegment}s/${document.uuid}`,
      {
        paidAt: data.paidAt,
      },
    ).then(({ status }) => {
      if (status === 204) {
        toast.success(t('markPaid.success.title'))
      } else {
        toast.error(t('markPaid.error.title'))
      }

      window.location.reload()
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex w-full items-end gap-2 sm:w-auto">
          <FormField
            control={form.control}
            name="paidAt"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1 sm:flex-none">
                {/* <FormLabel>{t('markPaid.form.paidAt.label')}</FormLabel> */}
                <FormControl>
                  <DatePicker
                    className="bg-background"
                    name={field.name}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    value={field.value ? toIsoDate(new Date(field.value)) : ''}
                    onChange={(value) => field.onChange(fromIsoDate(value)?.toISOString() ?? '')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DropdownMenu>
            <ButtonGroup>
              <Button type="submit">
                {t('markPaid.form.submit')}
              </Button>
              <DropdownMenuTrigger asChild>
                <Button className="px-2" aria-label="Další možnosti úhrady">
                  <Ellipsis className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </ButtonGroup>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  form.setValue(
                    'paidAt',
                    document.issuedAt || new Date().toISOString(),
                  )
                }}
              >
                {t('markPaid.form.whenIssued')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  form.setValue(
                    'paidAt',
                    document.deadlineAt || new Date().toISOString(),
                  )
                }}
              >
                {t('markPaid.form.onDeadline')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </form>
    </Form>
  )
}
